import { useEffect, useRef, useState } from "react";
import { authService } from "../../services/authService";
import { Button, Col, Flex, Form, Input, Row, Spin, Typography } from "antd";
import XdLogo from "../../assets/XdLogo.svg";
import styles from "./reset-password.module.scss";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faXmark, faCircleXmark } from '@fortawesome/free-solid-svg-icons';

const ResetPassword: React.FC = () => {
    type Status = "loading" | "valid" | "invalid";
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [status, setStatus] = useState<Status>("loading");
    const [tokenData, setToken] = useState<string>("");
    const hasFetched = useRef(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const verify = async () => {
            try {
                setLoading(true);
                const token = new URLSearchParams(window.location.search).get("token");

                if (!token) {
                    setStatus("invalid");
                    return;
                }

                setToken(token);

                const result = await authService.verifyToken(token);
                setStatus(result.valid ? "valid" : "invalid");

            } catch (err: any) {
                setStatus("invalid");
                setError(
                    err.response?.data
                    ? JSON.stringify(err.response.data, null, 2)
                    : err.message
                );
            } finally {
                setLoading(false);
            }
        };
        verify();
    }, []);

    const [isPasswordTouched, setIsPasswordTouched] = useState(false);
    const [passwordRules, setPasswordRules] = useState({
        length: false,
        lowercase: false,
        uppercase: false,
        number: false,
        special: false,
    });

    const handlePasswordChange = (value: string) => {
        setIsPasswordTouched(true);
        setPasswordRules({
            length: value.length >= 6,
            lowercase: /[a-z]/.test(value),
            uppercase: /[A-Z]/.test(value),
            number: /\d/.test(value),
            special: /[@$!%*?&]/.test(value),
        });

        const confirmPassword = form.getFieldValue('confirmPassword');
        if (confirmPassword) {
            setPasswordMatch(value === confirmPassword);
        }
    };

    const [passwordMatch, setPasswordMatch] = useState<boolean | null>(null);
    const handleConfirmPasswordChange = (value: string) => {
        const newPassword = form.getFieldValue('newPassword');

        if (!value) {
            setPasswordMatch(null);
        } else {
            setPasswordMatch(value === newPassword);
        }
    };

    const getIcon = (rule: boolean) => {
        if (!isPasswordTouched) return faXmark;
        return rule ? faCheck : faXmark;
    };

    const getClass = (rule: boolean) => {
        if (!isPasswordTouched) return styles.default;
        return rule ? styles.valid : styles.invalid;
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            await authService.resetPassword({newPassword: form.getFieldValue('newPassword'), token: tokenData});
            navigate('/reset-password-confirm');
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || null);
        } finally {
            setLoading(false);
        }
    };

    if (status === "loading") {
        return <div>Loading...</div>;
    }

    if (status === "invalid") {
        return (
            <>
                <div className={styles.pageContainer}>
                    <Row>
                        <Col xs={24} md={24} lg={24} className={styles.headerPage}>
                            <div className={styles.pageHeader}>
                                <span className={styles.xdLogo}>
                                <img
                                    src={XdLogo}
                                    alt="icon"
                                />
                            </span>
                            </div>
                        </Col>
                    </Row>
                    <Row className={styles.contentRow}>
                        <Col xs={24} md={24} lg={24} className={styles.resetPasswordConfirmPage}>
                            <div className={styles.resetPasswordHeader}>
                                {/* <Typography.Title level={3}>Reset password link</Typography.Title> */}
                                <Typography.Title level={4} style={{ color: '#99A0A7', fontWeight: 400 }}>
                                    This reset link is invalid or has expired.
                                </Typography.Title>
                                
                                <Typography.Title level={5} className={styles.backLink}>
                                    <Typography.Link href="/Login" style={{ fontWeight: 700 }} className={styles.link}> <FontAwesomeIcon icon={faArrowLeft} /> Back to Login</Typography.Link>
                                </Typography.Title>
                            </div>
                        </Col>
                    </Row>
                </div>
            </>
        );
    }

    return (
        <>

            <Spin spinning={loading} fullscreen />
            <div className={styles.pageContainer}>
                <Row>
                    <Col xs={24} md={24} lg={24} className={styles.headerPage}>
                        <div className={styles.pageHeader}>
                            <span className={styles.xdLogo}>
                                <img
                                    src={XdLogo}
                                    alt="icon"
                                />
                            </span>
                        </div>
                    </Col>
                </Row>
                <Row className={styles.contentRow}>
                    <Col xs={24} md={24} lg={24} className={styles.resetPasswordPage}>
                        <div className={styles.resetPasswordForm}>
                            {/* <span className={styles.xdLogo}>{XdLogoSvg()}</span> */}
                            <Typography.Title level={3}>Create New Password</Typography.Title>
                            <Typography.Text style={{ color: '#99A0A7', fontWeight: 400 }}>
                                This will only update your XD API and will not affect any other Xpressdocs affiliated accounts.
                            </Typography.Text>
                            {error && (
                                <Typography.Text className={styles.errorText}><FontAwesomeIcon icon={faCircleXmark} /> {error || "Incorrect New Password. Please try again."}</Typography.Text>
                            )}
                            <Form form={form} onFinish={handleSubmit}>
                                <Typography.Title level={5} className={styles.inputTitle}>
                                    New Password
                                </Typography.Title>
                                <Form.Item
                                    name="newPassword"
                                    style={{ marginBottom: 0 }}
                                    help={null}
                                    validateTrigger="onChange"
                                    rules={[
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.reject();

                                                    const isValid =
                                                        value.length >= 6 &&
                                                        /[a-z]/.test(value) &&
                                                        /[A-Z]/.test(value) &&
                                                        /\d/.test(value) &&
                                                        /[@$!%*?&]/.test(value);

                                                    return isValid
                                                        ? Promise.resolve()
                                                        : Promise.reject();
                                                },
                                            },
                                        ]}
                                >
                                    <Input.Password
                                        onChange={(e) => handlePasswordChange(e.target.value)}
                                        status={
                                                isPasswordTouched &&
                                                !(
                                                    passwordRules.length &&
                                                    passwordRules.lowercase &&
                                                    passwordRules.uppercase &&
                                                    passwordRules.number &&
                                                    passwordRules.special
                                                )
                                                    ? 'error'
                                                    : ''
                                            }
                                        placeholder="Enter new password"
                                    />
                                </Form.Item>

                                <div className={styles.passwordRules}>
                                    <div className={styles.fullRow}>
                                        <span className={getClass(passwordRules.length)}>
                                            <FontAwesomeIcon icon={getIcon(passwordRules.length)} />
                                        </span>
                                        <span>6 character minimum</span>
                                    </div>
                                    <div className={styles.gridRow}>
                                        <div>
                                            <span className={getClass(passwordRules.lowercase)}>
                                                <FontAwesomeIcon icon={getIcon(passwordRules.lowercase)} />
                                            </span>
                                            <span>Lowercase letter</span>
                                        </div>
                                        <div>
                                            <span className={getClass(passwordRules.uppercase)}>
                                                <FontAwesomeIcon icon={getIcon(passwordRules.uppercase)} />
                                            </span>
                                            <span>Uppercase letter</span>
                                        </div>
                                        <div>
                                            <span className={getClass(passwordRules.number)}>
                                                <FontAwesomeIcon icon={getIcon(passwordRules.number)} />
                                            </span>
                                            <span>Number</span>
                                        </div>
                                        <div>
                                            <span className={getClass(passwordRules.special)}>
                                                <FontAwesomeIcon icon={getIcon(passwordRules.special)} />
                                            </span>
                                            <span>Special character</span>
                                        </div>
                                    </div>
                                </div>
                                <Typography.Title level={5} className={styles.inputTitle}>
                                    Confirm New Password
                                </Typography.Title>
                                <Form.Item
                                    name="confirmPassword"
                                    dependencies={['newPassword']}
                                    style={{ marginBottom: 0 }}
                                    help={null}
                                    validateTrigger="onChange"
                                    rules={[
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value) return Promise.reject();
                                                    if (value === getFieldValue('newPassword')) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(); // no message
                                                },
                                            }),
                                        ]}
                                >
                                    <Input.Password
                                        placeholder="Confirm password"
                                        onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                                        status={
                                            passwordMatch === false ? 'error' : ''
                                        }
                                    />
                                </Form.Item>
                                <div className={styles.passwordMatch}>
                                    {passwordMatch === null ? null : passwordMatch ? (
                                        <p className={styles.valid}><FontAwesomeIcon icon={faCheck} /> Passwords match</p>
                                    ) : (
                                        <p className={styles.invalid}><FontAwesomeIcon icon={faXmark} /> Passwords do not match</p>
                                    )}
                                </div>
                                
                                <Flex vertical gap="small" style={{ width: "100%", padding: '20px 0px 0px 0px' }}>
                                    <Form.Item shouldUpdate>
                                        {() => {
                                            const hasErrors = form
                                                .getFieldsError()
                                                .some(({ errors }) => errors.length);

                                            const isTouched = form.isFieldsTouched(true);

                                            return (
                                                <Button
                                                    type="primary"
                                                    block
                                                    htmlType="submit"
                                                    disabled={!isTouched || hasErrors}
                                                    style={{ height: "36px", fontWeight: 700 }}
                                                >
                                                    Confirm New Password
                                                </Button>
                                            );
                                        }}
                                    </Form.Item>
                                </Flex>
                            </Form>
                            <Typography.Title level={5} className={styles.backLink}>
                                <Typography.Link href="/Login" style={{ fontWeight: 700 }} className={styles.link}> <FontAwesomeIcon icon={faArrowLeft} /> Back to Login</Typography.Link>
                            </Typography.Title>
                        </div>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default ResetPassword;
